/**
 * Hook para gerenciamento do estado de busca avançada
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { FileItem } from '../../../services/api'
import {
  SearchQuery,
  SearchResponse,
  SearchFilters,
  SearchScope,
  SearchResultCategory,
  SearchSuggestion,
  DEFAULT_SEARCH_QUERY,
  DEFAULT_SEARCH_SCOPE,
  DEFAULT_SEARCH_FILTERS
} from '../types/search.types'
import {
  executeSearch,
  executeSearchApi,
  getAutocompleteSuggestions,
  getAutocompleteSuggestionsApi,
  indexFileForSearch,
  clearSearchCache,
  getAllSpeakers
} from '../services/searchService'
import { Transcription, Entity, AISummary, UserAnnotations } from '../../../types/transcription'

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface UseSearchOptions {
  /** Arquivos disponíveis para busca */
  files: FileItem[]
  /** Debounce em ms para busca */
  debounceMs?: number
  /** Máximo de itens no histórico */
  maxHistoryItems?: number
  /** Callback para obter transcrição */
  getTranscription?: (fileId: string) => Promise<Transcription | null>
  /** Callback para obter entidades */
  getEntities?: (fileId: string) => Promise<Entity[] | null>
  /** Callback para obter resumo */
  getSummary?: (fileId: string) => Promise<AISummary | null>
}

interface UseSearchReturn {
  // Estado
  query: string
  filters: SearchFilters
  scope: SearchScope
  results: SearchResponse | null
  isSearching: boolean
  error: string | null
  activeCategory: SearchResultCategory
  isModalOpen: boolean
  searchHistory: string[]
  suggestions: SearchSuggestion[]
  availableSpeakers: string[]

  // Ações
  setQuery: (query: string) => void
  setFilters: (filters: Partial<SearchFilters>) => void
  setScope: (scope: Partial<SearchScope>) => void
  setActiveCategory: (category: SearchResultCategory) => void
  search: () => Promise<void>
  clearSearch: () => void
  openModal: () => void
  closeModal: () => void
  clearHistory: () => void

  // Indexação
  indexFile: (
    file: FileItem,
    transcription?: Transcription,
    entities?: Entity[],
    summary?: AISummary,
    annotations?: UserAnnotations
  ) => void

  // Resultados filtrados por categoria
  filteredResults: {
    transcripts: SearchResponse['results']['transcriptMatches']
    entities: SearchResponse['results']['entityMatches']
    files: SearchResponse['results']['fileMatches']
    summaries: SearchResponse['results']['summaryMatches']
    annotations: SearchResponse['results']['annotationMatches']
  }

  // Contadores por categoria
  categoryCounts: {
    all: number
    transcripts: number
    entities: number
    files: number
    summaries: number
    annotations: number
  }
}

// ═══════════════════════════════════════════════════════════════
// STORAGE KEYS
// ═══════════════════════════════════════════════════════════════

const HISTORY_STORAGE_KEY = 'monkee_search_history'

function loadSearchHistory(): string[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveSearchHistory(history: string[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history))
  } catch {
    // Ignore storage errors
  }
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export function useSearch(options: UseSearchOptions): UseSearchReturn {
  const {
    files,
    debounceMs = 300,
    maxHistoryItems = 20,
    getTranscription,
    getEntities,
    getSummary
  } = options

  // Estado principal
  const [query, setQueryState] = useState('')
  const [filters, setFiltersState] = useState<SearchFilters>(DEFAULT_SEARCH_FILTERS)
  const [scope, setScopeState] = useState<SearchScope>(DEFAULT_SEARCH_SCOPE)
  const [results, setResults] = useState<SearchResponse | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<SearchResultCategory>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchHistory, setSearchHistory] = useState<string[]>(loadSearchHistory)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])

  // Refs
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const lastQueryRef = useRef<string>('')
  const lastFiltersRef = useRef<SearchFilters>(DEFAULT_SEARCH_FILTERS)
  const lastScopeRef = useRef<SearchScope>(DEFAULT_SEARCH_SCOPE)

  // ═══════════════════════════════════════════════════════════════
  // SETTERS
  // ═══════════════════════════════════════════════════════════════

  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery)

    // Atualiza sugestões via API (assíncrono)
    if (newQuery.trim().length >= 2) {
      getAutocompleteSuggestionsApi(newQuery, 8).then(setSuggestions)
    } else {
      // Para queries curtas, usa histórico local
      const historySuggestions = searchHistory.slice(0, 5).map(h => ({
        text: h,
        type: 'history' as const
      }))
      setSuggestions(historySuggestions)
    }
  }, [searchHistory])

  const setFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }))
  }, [])

  const setScope = useCallback((newScope: Partial<SearchScope>) => {
    setScopeState(prev => ({ ...prev, ...newScope }))
  }, [])

  // ═══════════════════════════════════════════════════════════════
  // BUSCA
  // ═══════════════════════════════════════════════════════════════

  const search = useCallback(async () => {
    if (!query.trim()) {
      setResults(null)
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      const searchQuery: SearchQuery = {
        query,
        operator: 'AND',
        searchIn: scope,
        filters,
        page: 1,
        pageSize: 50,
        sortBy: 'relevance',
        sortOrder: 'desc'
      }

      // Usa busca via API (recomendado - dados isolados no backend)
      const response = await executeSearchApi(searchQuery)

      setResults(response)

      // Adiciona ao histórico se não existir
      if (query.trim() && !searchHistory.includes(query.trim())) {
        const newHistory = [query.trim(), ...searchHistory].slice(0, maxHistoryItems)
        setSearchHistory(newHistory)
        saveSearchHistory(newHistory)
      }

      // Atualiza refs para evitar buscas duplicadas
      lastQueryRef.current = query
      lastFiltersRef.current = { ...filters }
      lastScopeRef.current = { ...scope }

    } catch (err) {
      console.error('Search error:', err)
      setError('Erro ao executar busca. Tente novamente.')
    } finally {
      setIsSearching(false)
    }
  }, [query, scope, filters, searchHistory, maxHistoryItems])

  const clearSearch = useCallback(() => {
    setQueryState('')
    setResults(null)
    setError(null)
    setActiveCategory('all')
    setSuggestions([])
  }, [])

  const clearHistory = useCallback(() => {
    setSearchHistory([])
    saveSearchHistory([])
  }, [])

  // ═══════════════════════════════════════════════════════════════
  // MODAL
  // ═══════════════════════════════════════════════════════════════

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  // ═══════════════════════════════════════════════════════════════
  // INDEXAÇÃO
  // ═══════════════════════════════════════════════════════════════

  const indexFile = useCallback((
    file: FileItem,
    transcription?: Transcription,
    entities?: Entity[],
    summary?: AISummary,
    annotations?: UserAnnotations
  ) => {
    indexFileForSearch(file, transcription, entities, summary, annotations)
  }, [])

  // ═══════════════════════════════════════════════════════════════
  // DEBOUNCE PARA BUSCA AUTOMÁTICA
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Verifica se houve mudança na query, filtros ou escopo
    const queryChanged = query !== lastQueryRef.current
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(lastFiltersRef.current)
    const scopeChanged = JSON.stringify(scope) !== JSON.stringify(lastScopeRef.current)

    // Dispara busca se há query e algo mudou
    if (query.trim() && (queryChanged || filtersChanged || scopeChanged)) {
      debounceRef.current = setTimeout(() => {
        search()
      }, debounceMs)
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, filters, scope, debounceMs, search])

  // ═══════════════════════════════════════════════════════════════
  // RESULTADOS FILTRADOS
  // ═══════════════════════════════════════════════════════════════

  const filteredResults = useMemo(() => {
    if (!results) {
      return {
        transcripts: [],
        entities: [],
        files: [],
        summaries: [],
        annotations: []
      }
    }

    return {
      transcripts: results.results.transcriptMatches,
      entities: results.results.entityMatches,
      files: results.results.fileMatches,
      summaries: results.results.summaryMatches,
      annotations: results.results.annotationMatches
    }
  }, [results])

  const categoryCounts = useMemo(() => {
    if (!results) {
      return {
        all: 0,
        transcripts: 0,
        entities: 0,
        files: 0,
        summaries: 0,
        annotations: 0
      }
    }

    return {
      all: results.totalResults,
      transcripts: results.results.transcriptMatches.length,
      entities: results.results.entityMatches.length,
      files: results.results.fileMatches.length,
      summaries: results.results.summaryMatches.length,
      annotations: results.results.annotationMatches.length
    }
  }, [results])

  // ═══════════════════════════════════════════════════════════════
  // FALANTES DISPONÍVEIS
  // ═══════════════════════════════════════════════════════════════

  const availableSpeakers = useMemo(() => {
    return getAllSpeakers()
  }, [files])

  // ═══════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════

  return {
    // Estado
    query,
    filters,
    scope,
    results,
    isSearching,
    error,
    activeCategory,
    isModalOpen,
    searchHistory,
    suggestions,
    availableSpeakers,

    // Ações
    setQuery,
    setFilters,
    setScope,
    setActiveCategory,
    search,
    clearSearch,
    openModal,
    closeModal,
    clearHistory,

    // Indexação
    indexFile,

    // Resultados
    filteredResults,
    categoryCounts
  }
}

export default useSearch
